import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useStyles } from './MeteringPlans.styles'
import clsx from 'clsx'
import {
  CheckBoxFieldv2,
  InputStartAdornment,
  LabraToolTipV2,
  TextInputV4,
} from '@labrav/react-components'

import { cloneDeep, get, isEmpty } from 'lodash'
import { isNonEmptyNumber } from '@labrav/common'
import {
  AdditionalMeter,
  AzureOfferPlan,
  IncludedQuantity,
} from '../../../../../modules/privateOffer/reducer'
import { FormikProps, useFormikContext } from 'formik'
import { MsftCombinedFormState } from '../../../../../modules/microsoftCombinedForm/reducer'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { calculateBuyerPrice } from '@labrav/common'
import { DisplayValueData } from '../../../../PrivateOfferFormV2/AzureOffer/Plan/PlanRow/MeteringDimensions/MeteringDimensions'

export interface MeteringPlansProps {
  plan: AzureOfferPlan
  index: number
}

export const MeteringPlans = ({ plan, index }: MeteringPlansProps) => {
  const { classes } = useStyles()

  const { values, setFieldValue, errors } =
    useFormikContext() as FormikProps<MsftCombinedFormState>

  const meteringList = { ...plan.pricing.customMeters?.meters }
  const BuyerPrice = () => {
    return (
      <Typography className={clsx(classes.sectionText, classes.centerAlign)}>
        Price from buyer
        <LabraToolTipV2
          tooltipPlacement="top"
          description={
            'The “Price from buyer” is automatically calculated by applying the above markup % to the price to ISV.'
          }
        >
          <FontAwesomeIcon
            icon={faCircleInfo}
            className={classes.toolTipIcon}
          />
        </LabraToolTipV2>
      </Typography>
    )
  }
  const meteringColumn = [
    'Plans',
    'Unit',
    'Public price',
    'Price to ISV',
    <BuyerPrice />,
    'Quantity',
  ]

  const updatePlan = (meteringKey: string, updatedMeter: AdditionalMeter) => {
    if (plan?.pricing?.customMeters) {
      const customMetersData = {
        ...plan?.pricing?.customMeters,
        meters: {
          ...plan?.pricing?.customMeters.meters,
          [meteringKey]: updatedMeter,
        },
      }
      const updateplan = {
        ...plan,
        pricing: {
          ...plan.pricing,
          customMeters: {
            ...customMetersData,
          },
        },
      }
      const planList = cloneDeep(values.draftOffer.plans) as AzureOfferPlan[]
      planList[index] = {
        ...updateplan,
      }
      setFieldValue('draftOffer.plans', planList)
    }
  }

  const tableDataCellClass = clsx(classes.rowText, classes.tableCell)

  return (
    <div>
      <div className={classes.headingWrapper}>
        <div className={classes.plansHeading}>Metering plans</div>
        <div className={classes.description}>
          Enter the unit price you are offering to the ISV.
        </div>
      </div>

      <div>
        <TableContainer>
          <Table aria-label="simple table">
            <TableHead className={classes.tableHeader}>
              <TableRow>
                {meteringColumn.map((col, index) => (
                  <TableCell
                    key={`metering-col-${index}-${col}`}
                    className={clsx(classes.headerTitle, classes.tableCell)}
                    data-testid={'metering-column'}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {meteringList &&
                Object.keys(meteringList).map(meteringKey => {
                  const meter = { ...meteringList[meteringKey] }
                  const originalMeter =
                    plan?.originalPricing?.customMeters?.meters?.[meteringKey]
                  const meterErrorObject = get(
                    errors?.draftOffer?.plans,
                    `[${index}].pricing.customMeters.meters.[${meteringKey}]`,
                    {}
                  )

                  const updateMeter =
                    (
                      key: 'pricePerPaymentInUsd' | 'quantity' | 'isInfinite',
                      meterQuantityIndex?: number
                    ) =>
                    (
                      event: React.ChangeEvent<HTMLInputElement>,
                      checked?: boolean
                    ) => {
                      const updatedIncludedQuantities = [
                        ...meter.includedQuantities,
                      ]
                      if (meterQuantityIndex !== undefined) {
                        updatedIncludedQuantities[meterQuantityIndex] = {
                          ...updatedIncludedQuantities[meterQuantityIndex],
                          [key]:
                            key === 'isInfinite'
                              ? checked
                              : parseInt(event.target.value),
                          ...(key === 'isInfinite' && checked
                            ? { quantity: undefined }
                            : {}),
                        }
                      }
                      const updatedMeter = {
                        ...meter,
                        ...(key === 'pricePerPaymentInUsd'
                          ? {
                              pricePerPaymentInUsd:
                                event.target.value !== ''
                                  ? Number(event.target.value)
                                  : event.target.value,
                            }
                          : {}),
                        includedQuantities: [...updatedIncludedQuantities],
                      }
                      updatePlan(meteringKey, updatedMeter as AdditionalMeter)
                    }
                  const publicPrice = originalMeter?.pricePerPaymentInUsd
                  const markupPercentage = plan.markupPercentage
                  const buyerPrice = calculateBuyerPrice({
                    wholesalePrice: meter?.pricePerPaymentInUsd || 0,
                    percentage: markupPercentage || 0,
                  })

                  return (
                    <TableRow className={classes.row} key={meteringKey}>
                      <TableCell className={tableDataCellClass} width={'210px'}>
                        <div>{meter.meteringDimensionName}</div>
                      </TableCell>
                      <TableCell className={tableDataCellClass}>
                        <div>{meter.unitOfMeasure}</div>
                      </TableCell>
                      <TableCell className={tableDataCellClass} width={'124px'}>
                        <DisplayValueData
                          isPrice={true}
                          id="publicPrice"
                          value={publicPrice}
                          precision={5}
                        />
                      </TableCell>
                      <TableCell className={clsx(tableDataCellClass)}>
                        <TextInputV4
                          type="number"
                          title=""
                          placeHolder=""
                          isReadOnly={
                            !!meter.includedQuantities?.[0]?.isInfinite
                          }
                          errorText={
                            get(
                              meterErrorObject,
                              'pricePerPaymentInUsd',
                              ''
                            ) as string
                          }
                          isError={
                            !isEmpty(
                              get(
                                meterErrorObject,
                                'pricePerPaymentInUsd',
                                ''
                              ) as string
                            )
                          }
                          startAdornment={<InputStartAdornment text="$" />}
                          alignRight
                          isRequired={true}
                          value={
                            isNonEmptyNumber(meter.pricePerPaymentInUsd)
                              ? meter.pricePerPaymentInUsd
                              : ''
                          }
                          updatedValue={
                            isNonEmptyNumber(meter.pricePerPaymentInUsd)
                              ? meter.pricePerPaymentInUsd
                              : ''
                          }
                          onChange={updateMeter('pricePerPaymentInUsd')}
                          keyName={`draftOffer.plans.${index}.pricing.customMeters.meters.${meteringKey}.pricePerPaymentInUsd`}
                          variant="regularV2"
                          keyToPrevent={['+', '-', 'e']}
                          showSpinner={false}
                        />
                      </TableCell>
                      <TableCell className={tableDataCellClass}>
                        <DisplayValueData
                          isPrice={true}
                          id="buyerPrice"
                          value={buyerPrice}
                          precision={5}
                        />
                      </TableCell>
                      {meter.includedQuantities.map(
                        (
                          quantityItem: IncludedQuantity,
                          quantityIndex: number
                        ) => {
                          const errQuantity = get(
                            meterErrorObject,
                            `includedQuantities.[${quantityIndex}].quantity`,
                            ''
                          )

                          return (
                            <TableCell
                              className={clsx(
                                tableDataCellClass,
                                classes.textRight
                              )}
                              width={'220px'}
                              key={`quantity-${quantityItem?.billingTerm?.type}-${quantityItem?.billingTerm?.value ?? quantityIndex}`}
                            >
                              <div className={classes.tableInput}>
                                <TextInputV4
                                  type="number"
                                  title={''}
                                  placeHolder={''}
                                  alignRight
                                  errorText={(errQuantity || '') as string}
                                  isError={
                                    !quantityItem.isInfinite &&
                                    !isEmpty(errQuantity || '')
                                  }
                                  isReadOnly={quantityItem.isInfinite}
                                  isRequired={true}
                                  value={
                                    isNonEmptyNumber(quantityItem.quantity)
                                      ? quantityItem.quantity
                                      : ''
                                  }
                                  updatedValue={
                                    isNonEmptyNumber(quantityItem.quantity)
                                      ? quantityItem.quantity
                                      : ''
                                  }
                                  onChange={updateMeter(
                                    'quantity',
                                    quantityIndex
                                  )}
                                  keyName={`draftOffer.plans.${index}.pricing.customMeters.meters.${meteringKey}.includedQuantities.${quantityIndex}.quantity`}
                                  minNumber={0}
                                  variant="regularV2"
                                  showSpinner={false}
                                />
                                <div className={classes.checkbox}>
                                  <CheckBoxFieldv2
                                    keyName="isInfinite"
                                    value={quantityItem.isInfinite || false}
                                    color="default"
                                    checked={Boolean(
                                      quantityItem.isInfinite || false
                                    )}
                                    title={
                                      <span className={classes.checkboxTitle}>
                                        Unlimited
                                      </span>
                                    }
                                    name="isInfinite"
                                    id="isInfinite"
                                    onChange={updateMeter(
                                      'isInfinite',
                                      quantityIndex
                                    )}
                                    inputProps={
                                      {
                                        'data-testid': 'checkbox-item',
                                      } as React.InputHTMLAttributes<HTMLInputElement>
                                    }
                                  />
                                </div>
                              </div>
                            </TableCell>
                          )
                        }
                      )}
                    </TableRow>
                  )
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  )
}
